import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Observable } from 'rxjs';
import { ApiService } from './api/api.service';
import { LoggingService } from './logging.service';
import { NavigationService } from './navigation.service';
import { BaseModel } from '../types/generated.types';
import { AuthService } from './auth.service';
import { TenantConfigurationUi } from '../types/tenant.type';
import { TextHelper } from '../helpers/text.helper';
import { MapHelper } from '../helpers/map.helper';
import { Platform, FeatureFlags } from '../enums/generated.enums';
import { ModuleType, PageType, Tenants } from '../enums/page-type.enum';

@Injectable({ providedIn: 'root' })
export class TenantsService {
  private readonly apiService = inject(ApiService);
  private readonly loggingService = inject(LoggingService);
  private readonly navigationService = inject(NavigationService);
  private readonly authService = inject(AuthService);

  public showWorkflows: boolean;

  private tenantsSubject = new BehaviorSubject<BaseModel[]>([]);
  private selectedTenantSubject = new BehaviorSubject<BaseModel | null>(null);
  private tenantConfigurationsSubject = new BehaviorSubject<TenantConfigurationUi[]>([]);

  private tenantsById = new Map<string, BaseModel>();
  private tenantConfigurationsById = new Map<string, TenantConfigurationUi>();
  private tenantUrlNamesById = new Map<string, string>();
  private tenantIdsByUrlName = new Map<string, string>();

  get tenants(): BaseModel[] {
    return this.tenantsSubject.value;
  }

  get selectedTenant(): BaseModel {
    return this.selectedTenantSubject.value;
  }

  get selectedTenantId(): string | null {
    return this.selectedTenantSubject.value?.id ?? null;
  }

  get tenantConfigurations(): TenantConfigurationUi[] {
    return this.tenantConfigurationsSubject.value;
  }

  get isAllTenantsSelected(): boolean {
    return this.tenants?.length > 1 && this.selectedTenant?.id == null;
  }

  get selectedTenantPlatform(): Platform | null {
    return this.getTenantPlatform(this.selectedTenantId);
  }


  get tenants$(): Observable<BaseModel[]> {
    return this.tenantsSubject.asObservable();
  }

  get selectedTenant$(): Observable<BaseModel | null> {
    return this.selectedTenantSubject.asObservable();
  }

  get selectedTenantId$(): Observable<string | null> {
    return this.selectedTenantSubject.pipe(
      map(tenant => tenant?.id ?? null),
      distinctUntilChanged(),
    );
  }

  get tenantConfigurations$(): Observable<TenantConfigurationUi[]> {
    return this.tenantConfigurationsSubject.asObservable();
  }

  get hasBluePrismTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.BluePrism || t.platform === Platform.BluePrismNextGen);
  }

  get hasUiPathTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.UiPath);
  }

  get hasAutomationAnywhereTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.AutomationAnywhere);
  }

  get hasPowerAutomateTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.PowerAutomateCloud || t.platform === Platform.PowerAutomateDesktop);
  }

  get hasPowerAutomateDesktopTenant(): boolean {
    return this.tenantConfigurations.some(t => t.platform === Platform.PowerAutomateDesktop);
  }

  constructor() {
    this.showWorkflows = this.authService.hasFeatureFlag(FeatureFlags.Workflows);
  }

  async initializeTenants(): Promise<void> {
    try {
      const tenantsList = await this.apiService.tenants.getTenantsList();
      const filteredTenants = tenantsList.filter(t => !t.isDeleted);

      this.tenantsById = MapHelper.toDictionary(tenantsList, t => t.id);

      const configurations = await this.apiService.tenants.getTenantsConfigurationUi();
      this.tenantConfigurationsById = MapHelper.toDictionary(configurations, t => t.tenantId);

      const tenantUrlNames = filteredTenants?.map(t => ({
        id: t.id,
        urlName: TextHelper.getUrlNameFromLabel(t.label),
      })) ?? [];
      this.tenantUrlNamesById = MapHelper.toDictionary2(tenantUrlNames, i => i.id, i => i.urlName);
      this.tenantIdsByUrlName = MapHelper.toDictionary2(tenantUrlNames, i => i.urlName, i => i.id);

      this.tenantsSubject.next(filteredTenants);
      this.tenantConfigurationsSubject.next(configurations);
    } catch (error: any) {
      this.loggingService.logException(error);
      this.tenantsSubject.next([]);
      this.tenantConfigurationsSubject.next([]);
    }
  }

  selectTenantByUrlName(tenantUrlName: string | null): void {
    const tenantId = this.tenantIdsByUrlName.get(tenantUrlName);
    if (tenantId === this.selectedTenant?.id) {
      return;
    }

    this.selectTenantById(tenantId);
  }

  async selectTenant(tenantId: string): Promise<void> {
    const isSpecial = tenantId === Tenants.AllTenants || tenantId === Tenants.AllWorkflows;
    this.selectTenantById(isSpecial ? null : tenantId);
    const tenantUrlName = isSpecial ? tenantId : this.tenantUrlNamesById.get(tenantId);
    if (tenantId === Tenants.AllWorkflows) {
      await this.navigationService.navigateToOtherTenant(tenantUrlName, ModuleType.Workflows, PageType.Workflows);
    } else {
      await this.navigationService.navigateToOtherTenant(tenantUrlName);
    }
  }

  getTenantLabel(tenantId: string): string {
    if (!tenantId) return '';
    const tenant = this.tenantsById.get(tenantId);
    return tenant?.label ?? tenantId ?? 'Unknown tenant\'}';
  }

  getTenantPlatform(tenantId: string): Platform | null {
    if (!tenantId) return null;
    const tenant = this.tenantConfigurationsById.get(tenantId);
    return tenant?.platform ?? null;
  }

  isBluePrism(tenantId: string): boolean {
    return this.getTenantPlatform(tenantId) === Platform.BluePrism;
  }

  isBluePrismNextGen(tenantId: string): boolean {
    return this.getTenantPlatform(tenantId) === Platform.BluePrismNextGen;
  }

  isAnyBluePrism(tenantId: string): boolean {
    const platform = this.getTenantPlatform(tenantId);
    return platform === Platform.BluePrism || platform === Platform.BluePrismNextGen;
  }

  isUiPath(tenantId: string): boolean {
    return this.getTenantPlatform(tenantId) === Platform.UiPath;
  }

  isPowerAutomate(tenantId: string): boolean {
    const platform = this.getTenantPlatform(tenantId);
    return platform === Platform.PowerAutomateCloud || platform === Platform.PowerAutomateDesktop;
  }

  isPowerAutomateDesktop(tenantId: string): boolean {
    return this.getTenantPlatform(tenantId) === Platform.PowerAutomateDesktop;
  }

  isPowerAutomateCloud(tenantId: string): boolean {
    return this.getTenantPlatform(tenantId) === Platform.PowerAutomateCloud;
  }

  private selectTenantById(tenantId: string): void {
    const tenant = this.tenantsById.get(tenantId);
    this.selectedTenantSubject.next(tenant);
  }
}
